// =============================================================================
// JobFolderCard — T-506
//
// One button. Creates this job's working folder in SharePoint, named from ops,
// and from then on opens it.
//
// The point is that a human never decides a folder name again. That was the
// whole cause of the drift we cleaned up on 13/08: three naming conventions in
// one library, two folders filed under the wrong one, and three with no ops
// record at all — because it was a fresh decision every time.
//
// What goes in the folder: the working pile. Supplier quotes, RFQ replies, the
// gantt while it is being edited, and the client surveys too big to be worth
// putting in ops. The ops document library stays the RECORD — categorised,
// versioned, permissioned. This is the workbench beside it, not a rival to it.
//
// 25 Sep 2026 — James: "BIDs get a SP folder but when we move it to a project
// the folder stays in SP BID location and link is not moved, do I manually
// move folder in SP from BID > Projects and re-name?" No. The move has been in
// the Worker since T-506 (/sharepoint/folder/promote: server-side copy into
// the Projects library under the PROJECT's name, then delete the BID original)
// — but the only button for it sat on the BID page, and once a job is won
// nobody goes back there. So the PROJECT's card now offers it too: when the
// project has no folder and its source bid has one, the first choice is
// "Move the bid's folder here", ahead of creating a fresh, empty one.
// =============================================================================

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FolderPlus, FolderOpen, ExternalLink, Link2, FolderInput } from "lucide-react";
import { apiFetch, supabase } from "@/lib/supabase";
import { Card, Button, Input } from "@/components/ui";

export default function JobFolderCard({
  kind, id, folderUrl, canManage, onChanged, promotedProjectId, sourceBidId,
}: {
  kind: "bid" | "project";
  id: string;
  folderUrl: string | null;
  canManage: boolean;
  onChanged: () => void;
  // Set on a bid that has become a project. Everything gathered while chasing
  // the job is what the project runs on, so the folder travels rather than
  // being duplicated.
  promotedProjectId?: string | null;
  // 25 Sep 2026 — set on a PROJECT that came from a bid, so the card can offer
  // to move that bid's folder here rather than create an empty one.
  sourceBidId?: string | null;
}) {
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [pasted, setPasted]   = useState("");
  const [moveNote, setMoveNote] = useState<string | null>(null);

  // 25 Sep 2026 — on a project with no folder yet: does its source bid have one
  // to bring across? (Hook sits above every return, per the hooks rule.)
  const { data: sourceBid } = useQuery({
    queryKey: ["job_folder_source_bid", sourceBidId ?? null],
    enabled: kind === "project" && !folderUrl && !!sourceBidId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("bids")
        .select("bid_id, bid_number, sharepoint_folder_url, sharepoint_item_id")
        .eq("bid_id", sourceBidId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as { bid_id: string; bid_number: string | null; sharepoint_folder_url: string | null; sharepoint_item_id: string | null } | null;
    },
  });
  const bidFolderToMove = kind === "project" && !folderUrl && sourceBid?.sharepoint_item_id ? sourceBid : null;

  // Safe to click repeatedly: the Worker stores the in-flight copy and resumes
  // it rather than starting a second one, and never deletes the original until
  // the copy is confirmed landed. From a BID card the bid is `id`; from a
  // PROJECT card it is the source bid.
  async function moveToProject() {
    const bidId = kind === "bid" ? id : bidFolderToMove?.bid_id;
    if (!bidId) return;
    setBusy(true); setError(null); setMoveNote(null);
    try {
      const res = await apiFetch("/sharepoint/folder/promote", {
        method: "POST",
        body: JSON.stringify({ bid_id: bidId }),
      });
      const j = await res.json().catch(() => ({} as any));
      if (!res.ok) throw new Error(j?.detail ?? j?.error ?? `Couldn't move the folder (${res.status})`);
      setMoveNote(j?.detail ?? "Done.");
      if (j?.moved) onChanged();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally { setBusy(false); }
  }

  async function create() {
    setBusy(true); setError(null);
    try {
      const res = await apiFetch("/sharepoint/folder/create", {
        method: "POST",
        body: JSON.stringify({ kind, id }),
      });
      const j = await res.json().catch(() => ({} as any));
      if (!res.ok) throw new Error(j?.detail ?? j?.error ?? `Couldn't create the folder (${res.status})`);
      if ((j?.subfolders_missing ?? []).length) {
        window.alert(`Folder created, but these subfolders didn't: ${j.subfolders_missing.join(", ")}. Make them by hand if you need them.`);
      }
      onChanged();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally { setBusy(false); }
  }

  async function link() {
    setBusy(true); setError(null);
    try {
      const res = await apiFetch("/sharepoint/folder/link", {
        method: "POST",
        body: JSON.stringify({ kind, id, url: pasted.trim() }),
      });
      const j = await res.json().catch(() => ({} as any));
      if (!res.ok) throw new Error(j?.detail ?? j?.error ?? `Couldn't link that folder (${res.status})`);
      setLinking(false); setPasted("");
      onChanged();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally { setBusy(false); }
  }

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider2 text-text-muted mb-3">
          <FolderOpen size={12}/> Working folder
        </div>

        {folderUrl ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <a href={folderUrl} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-[13px] font-semibold
                          bg-ok/12 border border-ok/35 text-ok shadow-sm
                          hover:bg-ok/20 hover:border-ok/50 active:scale-[0.98]
                          transition-all duration-150">
              <FolderOpen size={14}/> Open working folder <ExternalLink size={11}/>
            </a>
            <span className="text-[11px] text-text-dim">in SharePoint</span>
            {kind === "bid" && promotedProjectId && canManage && (
              <Button size="sm" variant="secondary" icon={FolderInput}
                      onClick={moveToProject} disabled={busy}>
                {busy ? "Moving…" : "Move folder to project"}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="text-[12px] text-text-muted mb-2.5">
              Somewhere for supplier quotes, RFQ replies, drawings and the big client
              surveys — anything the team needs that isn't a controlled document.
            </div>
            {/* 25 Sep 2026 — the bid's folder is where the context is. Offer it first. */}
            {bidFolderToMove && (
              <div className="mb-2.5 border border-accent/40 bg-accent/10 rounded px-3 py-2 text-[12px] text-text">
                This job came from bid <span className="font-mono">{bidFolderToMove.bid_number ?? "—"}</span>, and that
                bid already has a working folder. Moving it brings everything gathered while chasing the job across
                and renames it to this project — nothing is left behind in the BID library.
              </div>
            )}
            {canManage && !linking && (
              <div className="flex flex-wrap gap-2">
                {bidFolderToMove && (
                  <Button size="sm" icon={FolderInput} onClick={moveToProject} disabled={busy}>
                    {busy ? "Moving…" : "Move the bid's folder here"}
                  </Button>
                )}
                <Button size="sm" icon={FolderPlus} variant={bidFolderToMove ? "secondary" : "primary"} onClick={create} disabled={busy}>
                  {busy ? "Creating…" : bidFolderToMove ? "Create a new, empty folder instead" : "Create folder"}
                </Button>
                <Button size="sm" variant="secondary" icon={Link2}
                        onClick={() => { setLinking(true); setError(null); }} disabled={busy}>
                  Link existing
                </Button>
              </div>
            )}
            {canManage && linking && (
              <div className="space-y-2">
                <div className="text-[11px] text-text-muted">
                  Paste the SharePoint address of the folder that already exists.
                </div>
                <Input value={pasted} onChange={(e: any) => setPasted(e.target.value)}
                       placeholder="https://coretelgroup.sharepoint.com/sites/CortelProjects/…"/>
                <div className="flex gap-2">
                  <Button size="sm" onClick={link} disabled={busy || !pasted.trim()}>
                    {busy ? "Linking…" : "Link it"}
                  </Button>
                  <Button size="sm" variant="secondary"
                          onClick={() => { setLinking(false); setPasted(""); setError(null); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {moveNote && (
          <div className="mt-2 bg-ok/10 border border-ok/30 rounded px-3 py-2 text-[12px] text-ok">
            {moveNote}
          </div>
        )}

        {error && (
          <div className="mt-2 bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
            {error}
          </div>
        )}
      </div>
    </Card>
  );
}
